import {useState, useEffect} from 'react';

import type {
  UseShippingEstimateProps as Props,
  ArtaShippingEstimate,
  ArtaLocation,
  AsyncState,
} from './typings';

import {pushGAManagedShippingEvent} from '@/utils/gtm';
import {
  fetchArtaEstimate,
  fetchArtaSession,
  getHostedSessionParams,
  getShippingEstimate,
} from './utils';

/**
 * @description custom hook to manage fetching the shipping estimates from Arta API.
 * - fetches session from Arta API
 * - provides a function to fetch shipping estimates
 * - manages the state of the session and estimate
 * @param {object} params - The parameters for the hook.
 * @param {object} params.origin - The origin location.
 * @param {object[]} params.objects - The list of product objects to ship.
 * @param {'CAD' | 'EUR' | 'GBP' | 'HKD' | 'USD'} params.currency - The currency for the shipping estimate.
 * @param {string} params.productSlug - The product slug, used for identifying the estimate.
 * @param {boolean|undefined} params.isEnabled - Flag to enable or disable the managed shipping functionality.
 * @param {'parcel' | 'premium' | 'select' | 'self_ship'[] | undefined } params.quoteTypes - Optional array of quote types to quote estimates.
 * @returns {object} An object containing:
 * -  the session state,
 * -  estimate state and a function to set estimate state,
 * -  the function to get estimates.
 */
const useShippingEstimate = ({
  origin,
  objects,
  currency,
  productSlug,
  isEnabled,
  quoteTypes,
}: Props) => {
  const [session, setSession] = useState<AsyncState<ReturnType<typeof getHostedSessionParams>>>({
    status: 'idle',
    data: null,
    error: null,
  });

  const [estimate, setEstimate] = useState<AsyncState<ArtaShippingEstimate>>({
    status: 'idle',
    data: null,
    error: null,
  });

  const handleGetEstimate = async (destination: ArtaLocation) => {
    if (!isEnabled) {
      return setEstimate({status: 'error', data: null, error: 'Managed shipping not enabled'});
    }
    if (session.data === null) {
      return setEstimate({status: 'error', data: null, error: 'Session not available'});
    }
    setEstimate((current) => ({...current, status: 'loading', error: null}));
    pushGAManagedShippingEvent({event: 'managed_shipping_request'});

    const {data: payload, error} = await fetchArtaEstimate({
      origin,
      objects,
      currency,
      destination,
      session: session.data,
      quoteTypes,
    });

    if (payload) {
      setEstimate({
        status: 'success',
        data: getShippingEstimate(payload, productSlug),
        error: null,
      });
      pushGAManagedShippingEvent({event: 'managed_shipping_estimate_received'});
    } else {
      setEstimate({status: 'error', data: null, error: error});
      pushGAManagedShippingEvent({event: 'managed_shipping_estimate_failed'});
    }
  };

  /**
   * @description Effect to fetch the Arta hosted session when the hook is initialized
   * and when the `isEnabled` flag changes.
   * - if `isEnabled` is false, do not fetch and set the session state to error.
   * - if `isEnabled` is true, fetch the session from Arta API and update the state accordingly.
   */
  useEffect(() => {
    if (!isEnabled) {
      return setSession({status: 'error', data: null, error: 'Managed shipping not enabled'});
    }
    const fetchSession = async () => {
      setSession((current) => ({...current, status: 'loading', data: null, error: null}));
      const {data: session, error} = await fetchArtaSession({origin, objects, currency});

      if (session) {
        setSession({status: 'success', data: getHostedSessionParams(session), error: null});
      } else {
        setSession({status: 'error', data: null, error: error});
      }
    };
    fetchSession();
  }, [isEnabled]);

  return {session, estimate, handleGetEstimate, setEstimate};
};

export default useShippingEstimate;
